import { useCallback, useId, useMemo, useRef } from 'react';
import { useAtomValue } from 'jotai';
import { HeartIcon } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import { TileOverlay } from '@rocco/ui/tile-shared/components/TileOverlay';
import { FollowSelector } from './FollowSelector';
import { useFollowSelectorContext } from '../context/FollowSelectorContext';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';
import { useGuestSave } from '~/modules/moodboards/hooks/use-guest-save';
import { isUserLoggedIn, useUser } from '@modules/user';
import type { PinItemType } from '~/modules/moodboards/type';
import { lastUsedBoardIdAtom } from '~/modules/moodboards/state/design-board-save-state';
import { buildThumbnail } from '../utils/build-sections';
import { useTracker } from '@archipro-website/tracker';

/**
 * Find the nearest ancestor that creates a new containing block for position:fixed.
 * CSS transforms, perspective, and filter on ancestors cause position:fixed to be
 * relative to that ancestor instead of the viewport (e.g. inside Swiper carousels).
 */
const findFixedContainingBlock = (element: HTMLElement): HTMLElement | null => {
    let parent = element.parentElement;
    while (parent && parent !== document.documentElement) {
        const { transform, perspective, filter } = getComputedStyle(parent);
        if (
            transform !== 'none' ||
            perspective !== 'none' ||
            (filter !== '' && filter !== 'none')
        ) {
            return parent;
        }
        parent = parent.parentElement;
    }
    return null;
};

interface TileOverlayWithFollowSelectorProps {
    /** The item ID to save. */
    pinId: number;
    /** The library item ID. Falls back to pinId if not provided. */
    libraryItemId?: number;
    /** The pin type (e.g. 'ProductPin', 'ProjectPin'). */
    pinType: PinItemType;
    /** Whether the overlay actions are disabled. */
    disabled?: boolean;
    /** Render a smaller heart button on mobile (for compact tile contexts like recently viewed). */
    compact?: boolean;
}

/**
 * Pinterest-style tile overlay that combines the presentational TileOverlay
 * with the FollowSelector board-picker and quick-save functionality.
 *
 * - Left dropdown: opens the FollowSelector popover for board selection.
 * - Right "Save" button: instantly saves to the latest (most recently used) board.
 * - Shows "Saved" state when the pin is already in a design board.
 */
export const TileOverlayWithFollowSelector = ({
    pinId,
    libraryItemId,
    pinType,
    disabled = false,
    compact = false,
}: TileOverlayWithFollowSelectorProps) => {
    const instanceId = useId();
    const tracker = useTracker();
    const user = useUser();
    const guestID =
        user.__typename === 'Me' ? user.TrackedGuest?.ID : undefined;
    const isLoggedIn = isUserLoggedIn(user);
    const { guestSave } = useGuestSave();
    const { isInDesignBoard, handleSaveToBoard, handleRemoveFromBoard } =
        useHandleFollow();
    const { openFollowSelector: contextOpen, openSelectorId } =
        useFollowSelectorContext();
    const {
        getSections,
        boards: rawBoards,
        moveToTop,
        getSavedBoardIdsForPin,
        ensureMembershipLoaded,
        isLoading: boardsLoading,
    } = useFollowSelectorContext();
    const anchorRef = useRef<HTMLSpanElement>(null);
    const dropdownButtonRef = useRef<HTMLButtonElement>(null);
    const lastUsedBoardId = useAtomValue(lastUsedBoardIdAtom);

    /** Whether this instance's FollowSelector popover is currently open. */
    const isFollowSelectorOpen = openSelectorId === instanceId;

    const isSaved = isInDesignBoard(pinId, pinType);

    const selectedItemIds = useMemo(
        () => [libraryItemId ?? pinId],
        [libraryItemId, pinId],
    );

    /**
     * Resolve the latest (most recently used) board from the sections.
     * Boards are sorted by LastActivity DESC, so the first board is the latest.
     *
     * Uses `getSections()` for the actual selection logic (popover interactions),
     * but the trigger label (defaultBoardName) is derived from `rawBoards`
     * so it stays stable during search.
     */
    const getLatestBoard = useCallback(() => {
        const sections = getSections();
        for (const section of sections) {
            if (section.items.length > 0) {
                return section.items[0];
            }
        }
        return null;
    }, [getSections]);

    /**
     * Resolve the board title shown in the overlay dropdown trigger.
     * - Guest → static "Save to Design Board".
     * - Logged-in, boards still loading → undefined (triggers shimmer).
     * - Logged-in, boards loaded but empty (new user) → "Save to Design Board".
     * - Logged-in, boards available → actual board title.
     */
    const defaultBoardName = useMemo(() => {
        if (!isLoggedIn) {
            return 'Save to Design Board';
        }

        const allBoards = getSections().flatMap((section) => section.items);

        if (lastUsedBoardId) {
            const lastUsedBoard = allBoards.find(
                (board) => board.id === lastUsedBoardId,
            );
            if (lastUsedBoard) {
                return lastUsedBoard.title;
            }
        }

        const latestTitle = getLatestBoard()?.title;
        if (latestTitle) return latestTitle;

        if (!boardsLoading) return 'Save to Design Board';
        return undefined;
    }, [
        isLoggedIn,
        getSections,
        lastUsedBoardId,
        getLatestBoard,
        boardsLoading,
    ]);

    /**
     * Position the anchor span at the target element's viewport location.
     * Compensates for CSS transform containers (e.g. Swiper) that make
     * position:fixed relative to the transformed ancestor instead of the viewport.
     */
    const positionAnchorAt = useCallback((element: HTMLElement) => {
        if (!anchorRef.current) return;
        const targetRect = element.getBoundingClientRect();
        const style = anchorRef.current.style;

        const containingBlock = findFixedContainingBlock(anchorRef.current);
        const cbRect = containingBlock?.getBoundingClientRect();
        const offsetX = cbRect?.left ?? 0;
        const offsetY = cbRect?.top ?? 0;

        style.top = `${targetRect.top - offsetY}px`;
        style.left = `${targetRect.left - offsetX}px`;
        style.width = `${targetRect.width}px`;
        style.height = `${targetRect.height}px`;
    }, []);

    /**
     * Quick-save or quick-unsave the pin.
     * - Not saved → save to the latest (most recently used) board.
     * - Already saved → remove from the latest board.
     * For guest users, shows the auth modal so they can log in first.
     */
    const handleQuickSave = useCallback(
        async (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            e.stopPropagation();

            if (!isLoggedIn) {
                guestSave({ pinId, pinType, libraryItemId });
                return;
            }

            if (isSaved) {
                // Quick-unsave: remove from the specific board this pin belongs to.
                let savedBoardIds = getSavedBoardIdsForPin(pinId, pinType);
                if (savedBoardIds.length === 0) {
                    savedBoardIds = await ensureMembershipLoaded(
                        pinId,
                        pinType,
                    );
                }

                if (savedBoardIds.length === 0) {
                    return;
                }

                const targetBoardId =
                    lastUsedBoardId && savedBoardIds.includes(lastUsedBoardId)
                        ? lastUsedBoardId
                        : savedBoardIds[0];

                const targetBoardFromSections = getSections()
                    .flatMap((section) => section.items)
                    .find((item) => item.id === targetBoardId);

                const targetBoardFromRawBoards = rawBoards.find(
                    (board) => board.id === targetBoardId,
                );

                const targetBoard =
                    targetBoardFromSections ??
                    (targetBoardFromRawBoards
                        ? {
                              id: targetBoardFromRawBoards.id,
                              title: targetBoardFromRawBoards.title,
                              projectName:
                                  targetBoardFromRawBoards.project?.title,
                              thumbnailUrls: targetBoardFromRawBoards.pins
                                  .map((pin) => pin.image)
                                  .filter(Boolean),
                              thumbnail: buildThumbnail(
                                  targetBoardFromRawBoards,
                              ),
                              entityType: 'moodboard' as const,
                              urlSegment: targetBoardFromRawBoards.urlSegment,
                              projectId: targetBoardFromRawBoards.project?.id,
                              projectUrlSegment:
                                  targetBoardFromRawBoards.project?.urlSegment,
                              isPersonal:
                                  targetBoardFromRawBoards.isPersonalMoodBoard,
                          }
                        : null);

                if (!targetBoard) {
                    return;
                }

                handleRemoveFromBoard(
                    targetBoard,
                    { id: pinId, libraryItemId },
                    pinType,
                );
                return;
            }

            const latestBoard =
                (lastUsedBoardId
                    ? getSections()
                          .flatMap((section) => section.items)
                          .find((item) => item.id === lastUsedBoardId)
                    : null) ?? getLatestBoard();

            if (!latestBoard) {
                // No boards available — open the selector at the dropdown trigger
                // so it appears in the same position as a regular dropdown click.
                if (dropdownButtonRef.current) {
                    positionAnchorAt(dropdownButtonRef.current);
                }
                contextOpen(instanceId, {
                    selectedItemIds,
                    selectedItemType: pinType,
                });
                return;
            }

            // Quick-save: add to the latest board
            handleSaveToBoard(
                latestBoard,
                { id: pinId, libraryItemId },
                pinType,
            );
            const itemId = libraryItemId ?? pinId;
            void tracker.log('SaveSubmit', {
                url: new URL(window.location.href),
                data: {
                    LibraryItemID: itemId,
                    ...(guestID ? { guest_id: guestID } : {}),
                },
            });
            moveToTop(latestBoard.id);
        },
        [
            tracker,
            isLoggedIn,
            isSaved,
            guestSave,
            pinId,
            pinType,
            libraryItemId,
            getLatestBoard,
            getSections,
            contextOpen,
            instanceId,
            selectedItemIds,
            handleSaveToBoard,
            handleRemoveFromBoard,
            moveToTop,
            getSavedBoardIdsForPin,
            ensureMembershipLoaded,
            rawBoards,
            lastUsedBoardId,
            guestID,
            positionAnchorAt,
        ],
    );

    const handleOverlayMouseEnter = useCallback(() => {
        if (!isLoggedIn || !isSaved) {
            return;
        }
        void ensureMembershipLoaded(pinId, pinType);
    }, [isLoggedIn, isSaved, ensureMembershipLoaded, pinId, pinType]);

    /**
     * Open the FollowSelector popover when the board dropdown is clicked.
     * For guest users, shows the auth modal so they can log in first.
     */
    const handleDropdownClick = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            e.stopPropagation();

            if (!isLoggedIn) {
                guestSave({ pinId, pinType, libraryItemId });
                return;
            }

            const libraryItemID = libraryItemId ?? pinId;
            tracker.log('PinMenuAdd', {
                url: new URL(window.location.href),
                data: {
                    LibraryItemID: libraryItemID,
                },
            });

            contextOpen(instanceId, {
                selectedItemIds,
                selectedItemType: pinType,
            });

            // Position the anchor at the clicked button
            positionAnchorAt(e.currentTarget);
        },
        [
            isLoggedIn,
            guestSave,
            pinId,
            libraryItemId,
            tracker,
            contextOpen,
            instanceId,
            selectedItemIds,
            pinType,
            positionAnchorAt,
        ],
    );

    return (
        <>
            {/* Mobile: heart button that opens the full-screen drawer */}
            <button
                type="button"
                disabled={disabled}
                onClick={handleDropdownClick}
                className={cn(
                    'absolute right-4 top-3.5 z-10 flex items-center justify-center rounded-full bg-white/70 backdrop-blur-sm md:hidden',
                    compact ? 'size-8' : 'size-[2.625rem]',
                )}
            >
                <HeartIcon
                    className={cn(
                        compact ? 'size-3.5' : 'size-4',
                        isSaved
                            ? 'fill-gray-900 stroke-gray-900'
                            : 'stroke-gray-900',
                    )}
                />
            </button>

            {/* Desktop: Pinterest-style overlay */}
            <span onMouseEnter={handleOverlayMouseEnter}>
                <TileOverlay
                    defaultBoardName={defaultBoardName}
                    boardNameLoading={isLoggedIn && !defaultBoardName}
                    isSaved={isSaved}
                    onSave={handleQuickSave}
                    onDropdownClick={handleDropdownClick}
                    dropdownRef={dropdownButtonRef}
                    disabled={disabled}
                    hideDropdownChevron={!isLoggedIn}
                    forceVisible={isFollowSelectorOpen}
                />
            </span>
            <FollowSelector
                instanceId={instanceId}
                anchorRef={anchorRef}
                pinId={pinId}
                libraryItemId={libraryItemId}
                pinType={pinType}
                popoverAlign="start"
            />
        </>
    );
};
